import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { lastValueFrom, Observable, throwError } from "rxjs";
import { catchError, first, map } from "rxjs/operators";
import { Store } from "@ngrx/store";

import { PlanFeatures, PlanStatus, SubscriptionPlan, SubscriptionPlanType } from "../../models/stripe.models";
import { DataService } from "./dataservices/data.service";
import { environment } from "src/environments/environment";
import { NotificationService } from "src/app/core/core-services/services/notification.service";
import { SharedDataService } from "./shared-data.service";
import { NotifyService } from "../../menu/not.service";
import { CookieService } from "ngx-cookie-service";
import { loadUserCurrentSubscriptionApi } from "../../store/plan/plan.actions";
import { PlanState } from "../../store/plan/plan.reducer";
import { ExtensionService } from "./extension.service";
import { MasqueradeUserResponse } from "src/app/core/models/masquerade.model";
import { AnalyticsService } from "src/app/analytics.service";
import { FlycutsFeatures } from "src/app/core/models/flycuts-features.model";
import { planTypeToIdentifier } from "src/app/utils/subscription-plan.utils";

// --- HMR SAFETY ---
// We need to declare a custom property on the window object to store the Stripe instance.
// This ensures that the instance persists across hot module reloads.
declare global {
  interface Window {
    StripeInstance: any;
  }
}
// --- END HMR SAFETY ---

@Injectable({
  providedIn: "root",
})
export class StripeService {
  private _stripe_secret = "";
  get stripe_secret(): string {
    return this._stripe_secret;
  }

  // This will now hold a single, persistent Stripe object.
  public stripe: any;

  constructor(
    private readonly dataService: DataService,
    private readonly extensionService: ExtensionService,
    private readonly notification: NotificationService,
    private readonly sharedData: SharedDataService,
    private readonly notifyService: NotifyService,
    private readonly cookieService: CookieService,
    private readonly analyticsService: AnalyticsService,
    private readonly store: Store<PlanState>,
    private readonly router: Router
  ) {
    // --- HMR SAFETY LOGIC ---
    // Check if the Stripe instance already exists on the window object.
    if (window.StripeInstance) {
      // If it exists, reuse it.
      this.stripe = window.StripeInstance;
    } else {
      // If it doesn't exist, create it and store it on the window object.
      this.stripe = Stripe(environment.STRIPE_KEY);
      window.StripeInstance = this.stripe;
    }
    // --- END HMR SAFETY LOGIC ---
  }

  /**
   * Transforms v2 FlycutsFeatures format to v1 PlanFeatures format for backward compatibility.
   * The v2 API returns features wrapped in { value: T, description: string } objects.
   */
  private adaptFlycutsFeatures(features: FlycutsFeatures): PlanFeatures {
    if (!features) {
      return null;
    }

    return {
      Categories: features?.categories_count?.value ?? 0,
      Subcategory: features?.subcategories_count?.value ?? 0,
      ShortcutVersionRollBack: features?.version_history_limit?.value ?? 0,
      "Number of FlyCuts that can be created": -1, // Not available in v2, set to unlimited
      "Number of Characters that can be used per flycut": features?.character_limit?.value ?? 0,
      "Rich Text Editor": "TinyMCE",
      "Spell Check (Language)": true,
      Bold: features?.bold?.value ?? false,
      Italic: features?.italic?.value ?? false,
      Underline: features?.underline?.value ?? false,
      Strikethrough: features?.strikethrough?.value ?? false,
      Hyperlink: features?.hyperlink?.value ?? false,
      "Alignment - Left": features?.alignment?.value?.includes('left') ?? false,
      "Alignment - Centered": features?.alignment?.value?.includes('center') ?? false,
      "Alignment - Right": features?.alignment?.value?.includes('right') ?? false,
      "Font Size": features?.font_size?.value?.length ?? 0,
      Fonts: features?.font_family?.value?.length ?? 0,
      "Bullet Points": features?.bullet_points?.value ?? false,
      "Numbered List": features?.numbered_list?.value ?? false,
      "Increase Indent": features?.indent?.value ?? false,
      "Decrease Indent": features?.outdent?.value ?? false,
      "Font Color": features?.font_color?.value ?? false,
      "Background Color": features?.background_color?.value ?? false,
      "Clear Formatting": features?.clear_formatting?.value ?? false,
      "Blocks (Headings)": features?.blocks?.value ?? false,
      Preview: features?.preview?.value ?? false,
      "Source Code": false, // Not available in v2
      "FlyCut Timeout": true, // Default to true
      "Right Click - Open Dashboard": true, // Default to true
      "FlyCuts Imports (JSON/CSV)": true, // Default to true
      "Search Bar": "Advanced",
      FlyPlates: features?.templates?.value ?? 0,
      "Latest FlyCuts": true, // Default to true
      Images: features?.images?.value ?? false,
      "Image Upload Size": this.parseStorageSize(features?.storage_limits?.value?.image_size),
      "Media Storage": this.parseStorageSize(features?.storage_limits?.value?.total_storage),
      "Domain Blacklists": "Unlimited",
      "Chat Support": { from: "9:00", to: "17:00", timezone: "EST" },
      "Email Support": { from: "9:00", to: "17:00", timezone: "EST" },
      Giphy: features?.giphys?.value ?? false,
      "AI Prompts": -1, // Not available in v2
      "FlyMSG AI Prompts Per Day": -1, // Not available in v2
    };
  }

  /**
   * Parses storage size string (e.g., "100KB", "5MB") to number in KB.
   */
  private parseStorageSize(sizeStr: string): number {
    if (!sizeStr) return 0;
    const match = sizeStr.match(/^(\d+(?:\.\d+)?)\s*(KB|MB|GB)?$/i);
    if (!match) return 0;
    const value = parseFloat(match[1]);
    const unit = (match[2] || 'KB').toUpperCase();
    switch (unit) {
      case 'GB': return value * 1024 * 1024;
      case 'MB': return value * 1024;
      case 'KB': default: return value;
    }
  }

  /**
   * Returns observable containing client_secret from payment intent
   * @returns {Observable<string>}
   */
  genPaymentIntent(): Observable<string> {
    return this.dataService.get({ url: "v1/subscriptions/payments/intent" }).pipe(
      map((response: any) => {
        return response.result.client_secret as string;
      })
    );
  }

  getCurrentPlan(): Observable<SubscriptionPlan> {
    return this.dataService.get({ url: "v2/plans/current" }).pipe(
      map((response: any) => {
        const result = response.result;
        if (result) {
          let identifier = result.identifier;

          if (result.identifier == "appsumo-growth-lifetime") {
            // identifier = "growth-yearly";
            this.storePlanInformation("planIdType", "Lifetime");
          }
          if (result.identifier == "dealfuel-growth-lifetime") {
            // identifier = "growth-yearly";
            this.storePlanInformation("planIdType", "dealfuel-growth-lifetime");
          }
          if (result.identifier == "sales-pro-yearly") {
            identifier = "sales-pro-yearly";
            this.storePlanInformation("planIdType", "sales-pro-yearly");
          }
          if (result.identifier == "sales-pro-monthly" || result.identifier == "sales-pro") {
            identifier = "sales-pro";
            this.storePlanInformation("planIdType", "sales-pro-monthly");
          }
          if (result.identifier == "pro-plan-teams-smb") {
            identifier = "pro-plan-teams-smb";
            this.storePlanInformation("planIdType", "pro-plan-teams-smb");
          }
          if (result.identifier == "pro-plan-teams-ent") {
            identifier = "pro-plan-teams-ent";
            this.storePlanInformation("planIdType", "pro-plan-teams-ent");
          }
          this.storePlanInformation("planId", identifier);
          if (["freemium", "starter", "growth"].includes(result.identifier)) {
            this.sharedData.setAttribute("planIdType", result.identifier);
          }
          this.sharedData.setAttribute("planId", identifier);
          this.sharedData.setAttribute("usedTrial", result?.used_trial);
          this.sharedData.setAttribute("onTrial", result?.on_trial);

          if (result?.referral_key && result?.referral_key != this.userDetails?.user_details?.referral_key) {
            const user = {
              ...this.userDetails,
              user_details: {
                ...this.userDetails?.user_details,
                referral_key: result?.referral_key,
              },
            };
            this.saveUserDetail(user);
          }

          let previousUserDetail = this.userDetails;
          let { user_details } = result;

          if (user_details) {
            this.analyticsService.identify(user_details.id);

            if (!user_details?.heap_analytics_id) {
              let interval = setInterval(async () => {
                const heapId = await this.analyticsService.userId();
                if (heapId.heap) {
                  const url = `v1/user/${user_details.id}/set_heap_id`;
                  lastValueFrom(
                    this.dataService.post({
                      url,
                      isLoader: false,
                      data: { heap_analytics_id: heapId.heap },
                    })
                  )
                    .then(() => {
                      clearInterval(interval);
                    })
                    .catch((error: any) => { });
                }
              }, 5000);
            }

            this.analyticsService.addUserProperties({
              email: user_details.email,
              hubspot_id: user_details.hubspot_id,
              first_name: user_details.first_name,
              last_name: user_details.last_name,
              plan: result.identifier,
              role: user_details.role
                ? user_details.role.indexOf("User") > -1
                  ? user_details.role
                  : `${user_details.role},User`
                : "User",
              signup_date: user_details.activation_date ?? user_details.created_at,
              company_name: user_details.company_name,
            });

            let updatedUserDetail = {
              ...previousUserDetail,
              user_details,
            };

            this.saveUserDetail(updatedUserDetail);
            this.extensionService.updateUserDetails(updatedUserDetail);
            this.notifyService.notifyAboutChange();
          }

          // this.grammarService.initialize();

          return {
            ...result,
            identifier: identifier,
            features: this.adaptFlycutsFeatures(result?.flycuts_features),
          };
        }
      })
    );
  }

  getDefaultPaymentMethod(loadedReturn: boolean = false): Observable<string | any> {
    return this.dataService.get({ url: "v1/subscriptions/payment/methods/default" }).pipe(
      map((response: any) => {
        const result = response.result;

        if (!result || !result.id) {
          return "";
        }

        if (loadedReturn) return result;

        return result.id as string;
      })
    );
  }

  setDefaultPaymentMethod(paymentMethodId: string): Observable<boolean> {
    const url = "v1/subscriptions/payment/methods/default";
    const data = {
      payment_method_id: paymentMethodId,
    };
    return this.dataService.post({ url, data, isLoader: false }).pipe(
      map(
        (response: any) => {
          const result = response.result;
          return result ? true : false;
        },
        (error) => {
          return false;
        }
      )
    );
  }

  subToPlan(paymentMethodId: string, plan: SubscriptionPlanType, discountCode?: string): Observable<boolean> {
    const url = "v1/subscriptions/payments";

    // The API is addressed by plan IDENTIFIER, not by the UI's plan type. Lower-casing
    // the type used to stand in for that mapping, which silently posted "sales-pro" —
    // a plan the backend does not have — for every monthly Sales Pro purchase (#424).
    const planIdentifier = planTypeToIdentifier(plan);

    if (!planIdentifier) {
      return throwError(() => new Error(`No backend plan identifier for plan "${plan}"`));
    }

    let data: any = {
      plan: planIdentifier,
      payment_method_id: paymentMethodId,
    };

    if (discountCode) {
      data.discount_code = discountCode;
    }
    // ignoreErrors: true — issue #609. The payments call now owns its own
    // 402/403/409/422 split (`classifyCheckoutError`, "known shapes only")
    // so the caller can show the backend's user-safe message instead of
    // DataService.handleError's generic fallthrough. The two shapes
    // DataService still owns — a 5xx and `403 {code: "DEACTIVATED"}` — are
    // replicated by the caller via a direct `dataService.handleError()`
    // call, exactly the way PR #606 did for `startSalesProTrial()`.
    return this.dataService.post({ url, data, isLoader: false, ignoreErrors: true }).pipe(
      map((response: any) => {
        const result = response.result;

        if (result) {
          this.applyPurchaseSideEffects(plan, result.data.identifier, paymentMethodId);
        }

        return result ? true : false;
      })
    );
  }

  /**
   * Issue #610 F-1 — the "purchase went through" side effects: the
   * analytics event, the plan-store refresh, `sharedData`, the extension
   * notify and the learning-icon reset. These used to live only inside
   * `subToPlan()`'s success `map()`, so they only ever ran for a plain 200
   * response. A purchase that instead completes via a 3-D Secure challenge
   * or the "still confirming" poll converging is never a 200 response to
   * THIS call — `subToPlan()`'s observable errors for those (402/403), and
   * the eventual success is discovered later by polling `getCurrentPlan()`
   * — so none of this ran, and Settings kept showing the pre-purchase plan
   * until a reload. `recordPurchaseSuccess()` below is the same call site
   * `PaymentDetailsComponent` reaches for those two paths once its poll
   * converges, so every purchase — plain, 3DS, or still-confirming — runs
   * this exactly once.
   */
  private applyPurchaseSideEffects(plan: SubscriptionPlanType, planIdentifier: string, paymentMethodId: string): void {
    this.analyticsService.track("subscription_purchased_FlyMSG", {
      plan: plan,
      plan_id_type: planIdentifier,
      payment_method_id: paymentMethodId,
    });

    this.store.dispatch(loadUserCurrentSubscriptionApi({ refetchFromApi: true }));
    this.sharedData.setAttribute("planId", plan);
    this.sharedData.setAttribute("planIdType", planIdentifier);
    this.notifyService.notifyAboutChange({
      plan: plan,
      planStatus: PlanStatus.Active,
    });
    this.sharedData.setAttribute("shownLearningIconIndicator", "false");
  }

  /**
   * Called once `PaymentDetailsComponent`'s post-payment poll confirms a
   * purchase that did NOT resolve as `subToPlan()`'s plain 200 — a 3-D
   * Secure approval (`completeCardAuthentication()`) or a "still
   * confirming" 403 that later converges (`enterCheckoutPending()`). Runs
   * the exact same side effects the plain-200 path runs (issue #610 F-1),
   * from those two call sites only, so the analytics event still fires
   * exactly once per purchase.
   */
  recordPurchaseSuccess(plan: SubscriptionPlanType, planIdentifier: string, paymentMethodId: string): void {
    this.applyPurchaseSideEffects(plan, planIdentifier, paymentMethodId);
  }

  pausePlan(): void {
    const url = "v1/subscriptions/cancel";
    this.dataService
      .get({ url })
      .pipe(first())
      .subscribe((subCancelled: any) => {
        this.notification.toast(subCancelled.result.message);
      });
  }

  resumePlan(): void {
    const url = "v1/subscriptions/resume";
    this.dataService
      .get({ url })
      .pipe(first())
      .subscribe((subResumed: any) => {
        this.notification.toast(subResumed.result.message);
      });
  }

  /**
   * `ignoreErrors` skips DataService's centralized error handling (the
   * toast + hard-navigate to `/session/error` on a 5xx) so a caller can
   * degrade gracefully instead — used by the auto-apply path on the
   * payment page so a coupon-service 5xx on page arrival falls back to
   * full price instead of ejecting the user (issue #592 F-4). The manual
   * Apply button leaves this false, keeping master's behavior unchanged.
   */
  /**
   * `isLoader` (issue #600 AC-6): background coupon validation — e.g. the
   * per-card pricing on `/pricing` — must never raise the app-wide
   * `ngx-ui-loader` overlay; the payment page's manual Apply keeps today's
   * default (`true`) so its request is still followed by a loader spinner.
   */
  retrieveCouponObject(couponCode: string, planIdentifier: string, ignoreErrors = false, isLoader = true): any {
    return this.dataService.post({
      url: "v1/subscriptions/coupon",
      data: { coupon: couponCode, plan_identifier: planIdentifier },
      ignoreErrors,
      isLoader,
    });
  }

  /**
   * `ignoreErrors: true` (issue #605): a contact-sales-only plan now refuses
   * this request with a 4xx (flymsg-be#986 sends `422
   * {result:{message,code:"plan_contact_sales_only"}}`), and `DataService`'s
   * own centralized `handleError` is either silent for that shape (any 4xx
   * outside its 403/400-specific branches) or would risk toasting twice for
   * one it already recognizes. Suppressing it here and owning the whole
   * refusal in `catchError` below guarantees every one of the five callers
   * of this method gets exactly one message, regardless of which shape the
   * backend used — see the "Contracts pinned" table on issue #605.
   */
  startSalesProTrial() {
    return this.dataService.post({ url: "v1/subscriptions/trial", isLoader: true, ignoreErrors: true }).pipe(
      map((response: any) => {
        this.notification.toast("14-day Professional Plan Trial Successfully Added", "Ok");
        this.storePlanInformation("planId", SubscriptionPlanType.SalesPro);
        this.notifyService.notifyAboutChange();
        this.sharedData.setAttribute("planId", SubscriptionPlanType.SalesPro);
        this.sharedData.setAttribute("planIdType", "sales-pro-monthly");
        this.notifyService.notifyAboutChange({
          plan: SubscriptionPlanType.SalesPro,
          planStatus: PlanStatus.Trial,
        });
        this.extensionService.sendUpdateMessage();
      }),
      catchError((error: any) => {
        const status = typeof error?.status === "number" ? error.status : undefined;
        const body = error?.error;

        if (status !== undefined && status >= 500) {
          // Same copy + destination `DataService.handleError` uses for every
          // other endpoint's 5xx (issue #605 AC-6: this path is unchanged —
          // `ignoreErrors` above only suppresses it so this method can own
          // the 4xx split without a double toast, so it's replicated here).
          this.notification.toast("Technical Error! Please contact administrator.", "OK", 10000);
          this.router.navigate(["/session/error"]);
        } else if (status === 403 && body?.code === "DEACTIVATED") {
          // QA #606 F-3: `ignoreErrors` above stops `DataService.handleError`
          // from running its DEACTIVATED branch for this endpoint too — a
          // deactivated account must still be stopped here, not just on its
          // next unrelated request. Reuses the exact dialog + sign-out flow
          // every other endpoint takes.
          this.dataService.handleDeactivatedAccount(error);
        } else if (body?.error_list) {
          // QA #606 F-4: a validation 422 keeps its own existing text
          // (the toast/dialog split `DataService.handleError` already has),
          // never the generic fallback copy below.
          this.dataService.handleValidationErrorList(body.error_list);
        } else if (status !== undefined && status >= 400) {
          this.notification.toast(this.trialRefusalMessage(status, body), "OK", 6000);
        }
        return throwError(() => error); // You can re-throw the error or handle it here.
      })
    );
  }

  /**
   * QA #606 round-2 Decision (F-6/F-7): the "sales team" copy is shown ONLY
   * for the 422 `plan_contact_sales_only` refusal — never as a generic
   * fallback. A 403's `result.message` is shown verbatim ONLY when it is
   * byte-for-byte the used-trial refusal captured on the wire from #987
   * (`{"result":{"message":"You have used your trial period for this plan
   * already!"}}`, no `code`); every other 403 has the exact same envelope
   * shape (`{"result":{"message": <string>}}`) — including Stripe's raw
   * idempotency text replayed as a 403 (QA S-26) — so the message text
   * itself, pinned from the wire capture, is what distinguishes the real
   * refusal from an arbitrary one. DEACTIVATED (403 `code: "DEACTIVATED"`)
   * never reaches this method: `startSalesProTrial()` returns earlier for
   * it. Every other 4xx this method sees — a 422 without the known code
   * (the real "not eligible" refusal a second tab gets after tab A's trial
   * lands, or Stripe's raw idempotency text on a race loser, QA #606
   * S-14/S-22), a 403 with different text, or any other 4xx (429, 400,
   * 409, QA S-12/S-26) — now gets this neutral fallback instead.
   */
  private static readonly NEUTRAL_TRIAL_REFUSAL_MESSAGE =
    "We couldn't start your trial. Please refresh the page and try again.";

  private static readonly USED_TRIAL_REFUSAL_MESSAGE =
    "You have used your trial period for this plan already!";

  private trialRefusalMessage(status: number, body: any): string {
    const message = body?.result?.message;
    if (
      status === 422 &&
      typeof message === "string" &&
      body?.result?.code === "plan_contact_sales_only"
    ) {
      return message;
    }
    if (status === 403 && message === StripeService.USED_TRIAL_REFUSAL_MESSAGE) {
      return message;
    }
    return StripeService.NEUTRAL_TRIAL_REFUSAL_MESSAGE;
  }

  removePlanInformation() {
    this.sharedData.deleteAttribute("planId");
    this.sharedData.deleteAttribute("planIdType");
    this.cookieService.delete("flymsg_planId", "/", undefined, true, "None");
    this.cookieService.delete("flymsg_planIdType", "/", undefined, true, "None");
  }

  private storePlanInformation(key: string, value: any) {
    this.sharedData.setAttribute(key, value);
    this.cookieService.set(`flymsg_${key}`, value, {
      domain: environment.FLYMSG_DOMAIN,
      path: "/",
      secure: true,
      sameSite: "None",
    });
  }

  private get masqueradeUser(): MasqueradeUserResponse {
    const masqueradeUser = sessionStorage.getItem("fms-masquerade-user-details");
    return masqueradeUser && masqueradeUser !== 'null' && masqueradeUser !== 'undefined'
      ? (JSON.parse(masqueradeUser) as unknown as MasqueradeUserResponse)
      : null;
  }

  private get accessToken(): string {
    if (this.masqueradeUser) {
      return this.masqueradeUser.access_token;
    }

    return this.sharedData.getAttribute("accessToken");
  }

  private get refreshToken(): string | null {
    if (this.masqueradeUser) {
      return null;
    }

    return this.sharedData.getAttribute("refreshToken");
  }

  private get userDetails() {
    if (this.masqueradeUser) {
      return this.masqueradeUser;
    }

    return this.sharedData.getAttribute("userDetails");
  }

  private saveUserDetail(result: any) {
    this.sharedData.setAttribute("userDetails", result);
    this.sharedData.setAttribute("accessToken", result.access_token);
    this.sharedData.setAttribute("refreshToken", result.refresh_token);
    if (this.masqueradeUser) {
      this.sharedData.setAttribute(this.masqueradeUser.user_details.id, result);
    }
    this.store.dispatch(loadUserCurrentSubscriptionApi({}));
  }
}
